import { Bookmark } from 'lucide-react-native';
import type { PressableStateCallbackType } from 'react-native';

import { Pressable } from '@/components/ui/pressable';
import { useToggleSavedPost } from '@/features/inquiry-library';
import type { EngageableLibraryPost } from '@/features/inquiry-library/model';
import { useColors } from '@/hooks/useColors';
import { useHaptic } from '@/hooks/useHaptic';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

/**
 * Save/unsave toggle for a post found through a Research result, so a reader
 * can bookmark it without leaving the conversation to open the full Inquiry
 * Library post screen. Icon-only, matching the bookmark treatment
 * `CommunityActionBar` and `CommunityWrapper` already use elsewhere.
 */
export function SaveResultButton({ post }: { post: EngageableLibraryPost }) {
  const { colors } = useColors();

  const haptic = useHaptic();

  const { saved, toggleSaved } = useToggleSavedPost({ item: post, source: 'library_chat' });

  return (
    <Pressable
      hitSlop={ICON_BUTTON_HIT_SLOP}
      className="h-10 w-10 items-center justify-center"
      onPress={() => {
        haptic.impactLight();

        toggleSaved();
      }}
      {...getAccessibilityProps({
        label: saved ? 'Remove inquiry from saved' : 'Save inquiry',
        role: 'button',
      })}
      accessibilityState={{ selected: saved }}>
      {({ pressed }: PressableStateCallbackType) => (
        <Bookmark
          opacity={pressed ? 0.5 : 1}
          color={colors.primary[600]}
          size={24}
          fill={saved ? colors.primary[600] : colors.transparent}
        />
      )}
    </Pressable>
  );
}
